import { MutationTree }                               from 'vuex';
import { cloneDeep }                                  from 'lodash-es';
import { FilterConfiguration, ScrollPosition, State } from '@/components/chart/chart-wrapper/store/chart-wrapper.state.store';
import { ChartDataResponse }                          from '@/components/chart/chart.types';
import { DataZoomAxis, Default }                      from '@/components/chart/helpers/chart-variables';
import { ObjectWithStringKeys }                       from '@/types';

const mutations: MutationTree<State> = {
  setChartUuid(state: State, chartUuid: string): void {
    state.chartData.chartUuid = chartUuid;
  },
  setChartData(state: State, chartDataCollection: ChartDataResponse[]): void {
    state.chartData.chartDataCollection = chartDataCollection;
  },
  setChartLoader(state: State, isLoading: boolean): void {
    state.chartData.isLoading = isLoading;
  },
  setDataZoom(state: State, dataZoom: ScrollPosition<DataZoomAxis>): void {
    if (dataZoom.start != null) {
      state.dataZoom[ dataZoom.axis as DataZoomAxis ].start = dataZoom.start;
    }

    if (dataZoom.end != null) {
      state.dataZoom[ dataZoom.axis as DataZoomAxis ].end = dataZoom.end;
    }
  },
  setDisabledSeries(state: State, legend: ObjectWithStringKeys<boolean>): void {
    state.legendSeries = legend;
  },
  setIsLogarithmicAxis(state: State, value: boolean): void {
    state.isLogarithmicAxis = value;
  },
  setIsScrollRight(state: State, value: boolean): void {
    state.dataZoom.yAxis.start = 0;
    state.dataZoom.yAxis.end = 100;
    state.dataZoom.xAxis.end = 100;
    state.isScrollRight = value;
  },
  setIsTableView(state: State, value: boolean): void {
    state.isTableView = value;
  },
  setInitialChartSettings(state: State, payload: ObjectWithStringKeys<boolean>): void {
    state.initialIsLogarithmicAxis = payload.logarithmic;
    state.initialIsScrollRight = payload.scrollToRight;
    state.initialIsTableView = payload.tableView;
  },
  setFilterAllConfigurations(state: State, payload: FilterConfiguration[]): void {
    const defaultConfiguration: FilterConfiguration = payload.find((config: FilterConfiguration) => config.name === Default) as FilterConfiguration;
    const configsWithoutDefault: FilterConfiguration[] = payload
      .filter((config: FilterConfiguration) => config.name !== Default)
      .sort((a: FilterConfiguration, b: FilterConfiguration) => a.name.localeCompare(b.name));

    state.filterConfigurations = [ defaultConfiguration, ...configsWithoutDefault ];
  },
  setFilterConfiguration(state: State, payload: FilterConfiguration): void {
    const configuration: FilterConfiguration | undefined = state.filterConfigurations.find((configuration: FilterConfiguration) => configuration.name === payload.name);
    if (configuration) {
      configuration.json = cloneDeep(JSON.parse(payload.json as string));
    }
  },
  addFilterConfiguration(state: State, payload: FilterConfiguration): void {
    const allConfigs: FilterConfiguration[] = [ ...state.filterConfigurations, payload ];

    if (payload.name === Default) {
      state.filterConfigurations = [ payload, ...state.filterConfigurations ];
    } else {
      const defaultConfiguration: FilterConfiguration | undefined = state.filterConfigurations.find((config: FilterConfiguration) => config.name === Default);
      const configsWithoutDefault: FilterConfiguration[] = allConfigs
        .filter((config: FilterConfiguration) => config.name !== Default)
        .sort((a: FilterConfiguration, b: FilterConfiguration) => a.name.localeCompare(b.name));
      if (defaultConfiguration) {
        state.filterConfigurations = [ defaultConfiguration, ...configsWithoutDefault ];
      } else {
        state.filterConfigurations = [ ...configsWithoutDefault ];
      }
    }
  },
  removeFilterConfiguration(state: State, payload: string): void {
    state.filterConfigurations = state.filterConfigurations.filter((configuration: FilterConfiguration) => configuration.name !== payload);
  },
  setFilterActiveConfiguration(state: State, payload: string): void {
    state.filterActiveConfiguration = payload;
  },
  setChartHeight(state: State, height: number): void {
    state.chartHeight = height;
  },
};

export default mutations;
